import { FolderOpen, Info, Play, Plus, Settings } from 'lucide-preact'
import { text } from '#contract'
import { useClient } from '../app/client-context.ts'
import { useScreenStack } from '../app/screen-stack.ts'
import { useView } from '../app/use-view.ts'
import { Banner } from '../components/banner.tsx'
import { Button } from '../components/button.tsx'
import { ScreenPanel } from '../components/screen-panel.tsx'
import { useLocale, useT } from '../i18n/index.ts'
import { Text } from '../i18n/text.tsx'
import styles from './main-menu-screen.module.css'
import { newestSave, savedWhen } from './save-slots/slot-text.ts'

/**
 * The first screen after a load, drawn over the live level: Continue (the newest save), New
 * game and Load game, then Settings and Credits. With no saves, New game alone leads.
 */
export function MainMenuScreen() {
  const t = useT()
  const locale = useLocale()
  const client = useClient()
  const { push } = useScreenStack()
  const level = useView((view) => view.level)
  const notice = useView((view) => view.notice)
  const slots = useView((view) => view.saves.slots)
  const newest = newestSave(slots)
  const when = newest && savedWhen(locale, newest)

  // With every slot empty there's nothing to choose between: the first one it is.
  const newGame = (): void => {
    if (slots.every((slot) => slot.state === 'empty')) client?.saves.newGame(1)
    else push('newGame')
  }

  return (
    <ScreenPanel
      title={t(text('app.title'))}
      footer={
        <>
          <Button
            variant="ghost"
            icon={Settings}
            onClick={() => {
              push('settings')
            }}
          >
            {t(text('menu.settings'))}
          </Button>
          <Button
            variant="ghost"
            icon={Info}
            onClick={() => {
              push('credits')
            }}
          >
            {t(text('menu.credits'))}
          </Button>
        </>
      }
    >
      {notice && (
        <Banner>
          <Text value={notice} />
        </Banner>
      )}
      {level && (
        <p class={styles.level}>
          <Text value={level.name} />
        </p>
      )}
      <div class={styles.actions}>
        {newest && (
          <Button
            icon={Play}
            autoFocus
            {...(when ? { detail: t(when) } : {})}
            onClick={() => client?.saves.continueLatest()}
          >
            {t(text('menu.continue'))}
          </Button>
        )}
        <Button
          variant={newest ? 'ghost' : 'primary'}
          icon={Plus}
          autoFocus={!newest}
          onClick={newGame}
        >
          {t(text('menu.newGame'))}
        </Button>
        {newest && (
          <Button
            variant="ghost"
            icon={FolderOpen}
            onClick={() => {
              push('loadGame')
            }}
          >
            {t(text('menu.loadGame'))}
          </Button>
        )}
      </div>
    </ScreenPanel>
  )
}
